// Import global CSS before App to ensure any library CSS is specified in the correct order
import '../global.scss';
import React from 'react';
import * as ReactDOM from 'react-dom/client';
import { ApolloProvider } from '@apollo/client';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import { loadableReady } from '@loadable/component';
import { FeatureFlagResults } from '@moda/portal-stanchions';
import { HelmetProvider } from 'react-helmet-async';
import { initPerfume } from 'perfume.js';
import { CONFIG } from '../config';
import { identifyUser } from '../lib/identifyUser';
import { syncDatadogRum } from '../lib/datadogRum';
import { clearFeatureFlagTrackingCache } from '../lib/trackFeatureFlagView';
import { createClientSideApolloClient } from '../lib/apollo/client';
import { isDevelopment, isLocalhost } from '../lib/isEnv';
import { tc } from '../lib/trackingContext';
import { makeGoogleAnalyticsAdjustments } from '../lib/googleAnalytics/googleAnalyticsAdjustments';
import { getUserFromCookies } from '../hooks/useUser';
import { getCookies } from '../hooks/useCookies';
import { BreakpointProvider } from '../components/BreakpointProvider';
import { DiscoveryFeatureFlagProvider } from '../components/DiscoveryFeatureFlagProvider';
import { App } from '../App';
import { initializeDataGrail, determinePolicy } from '../lib/datagrail/datagrail';
import {
  installSegmentQueue,
  loadSegmentWithTags,
  loadSegmentWithoutTags
} from '../lib/segment/segment';
import {
  applyGTMConsentDefaults,
  applyGTMConsentUpdate
} from '../lib/googleAnalytics/googleTagManager';
import {
  applyGA4ConsentUpdate,
  createGA4DeviceMode
} from '../lib/googleAnalytics/ga4/ga4DeviceMode';
import { setAnalyticsConsentGranted } from '../lib/googleAnalytics/ga4/ga4State';

// TODO: move this polyfill to it's own chunk and load it only when required
import 'intersection-observer';
import 'isomorphic-fetch';
import 'focus-visible';

declare global {
  interface Window {
    analytics: AnalyticsBrowser;
    __FEATURE_FLAGS__?: FeatureFlagResults;
    __SERVER_ERROR__?: boolean;
    __SSR_WIDTH__?: number;
  }
}

const getRootElement = () => {
  const el = document.getElementById('root');
  if (!el) throw new Error('#root element not found');
  return el;
};

const history = createBrowserHistory();
const client = createClientSideApolloClient(history);

history.listen(() => clearFeatureFlagTrackingCache());

if (!isLocalhost()) {
  initializeDataGrail();
}
applyGTMConsentDefaults();

const { modaAnonymousId } = getCookies();

let analyticsConsentGranted = false;

const syncDatadogRumState = () =>
  void syncDatadogRum({
    consentGranted: analyticsConsentGranted,
    flags: window.__FEATURE_FLAGS__,
    user: getUserFromCookies()
  });

window.addEventListener('resetcache', syncDatadogRumState);

installSegmentQueue();

function instrumentWithConsent() {
  analyticsConsentGranted = true;
  setAnalyticsConsentGranted(true);
  applyGTMConsentUpdate(true);
  applyGA4ConsentUpdate(true);
  loadSegmentWithTags(modaAnonymousId);
}

const instrumentWithoutConsent = () => {
  analyticsConsentGranted = false;
  setAnalyticsConsentGranted(false);
  applyGTMConsentUpdate(false);
  applyGA4ConsentUpdate(false);
  loadSegmentWithoutTags(modaAnonymousId);
};

const ClientSideApp = (
  <DiscoveryFeatureFlagProvider>
    <Router history={history}>
      <ApolloProvider client={client}>
        <HelmetProvider>
          <BreakpointProvider ssrWidth={window.__SSR_WIDTH__}>
            <App error={window.__SERVER_ERROR__} />
          </BreakpointProvider>
        </HelmetProvider>
      </ApolloProvider>
    </Router>
  </DiscoveryFeatureFlagProvider>
);

const init = () => {
  if (isDevelopment()) {
    ReactDOM.createRoot(getRootElement()).render(ClientSideApp);
  } else {
    loadableReady(() => ReactDOM.hydrateRoot(getRootElement(), ClientSideApp));
  }
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

determinePolicy()
  .then(determination => {
    let consentGranted = false;
    let reason: 'consent' | 'no_consent' | 'empty_policy' = 'no_consent';

    switch (true) {
      case determination.policy.length === 0:
        reason = 'empty_policy';
        instrumentWithoutConsent();
        break;
      case determination.policy.includes("don't load tags"):
        reason = 'no_consent';
        instrumentWithoutConsent();
        break;
      case determination.policy.includes('load tags'):
        consentGranted = true;
        reason = 'consent';
        instrumentWithConsent();
        break;
      default:
        reason = 'no_consent';
        instrumentWithoutConsent();
    }

    tc.track('Consent Determined', {
      addToTrackingEvent: { consentGranted, reason }
    });
  })
  .catch(() => {
    instrumentWithoutConsent();
    tc.track('Consent Determined', {
      addToTrackingEvent: { consentGranted: false, reason: 'error' }
    });
  })
  .finally(async () => {
    const measurementID = CONFIG['GA4_MEASUREMENT_ID'];
    if (measurementID != null && measurementID !== '' && analyticsConsentGranted) {
      // Register GA4 plugin
      await window.analytics.register(createGA4DeviceMode());
    }

    identifyUser(getUserFromCookies());
    syncDatadogRumState();

    makeGoogleAnalyticsAdjustments();

    const events = {
      cls: 'Cumulative Layout Shift',
      lcp: 'Largest Contentful Paint',
      fidVitals: 'First Input Delay'
    } as const;
    function isValidEvent(event: string): event is keyof typeof events {
      return Object.keys(events).includes(event);
    }

    try {
      initPerfume({
        analyticsTracker: options => {
          const { data, metricName, navigatorInformation, rating } = options;
          const eventData = {
            category: 'Performance',
            // eslint-disable-next-line @typescript-eslint/no-magic-numbers
            value: metricName === 'cls' ? Math.round(Number(data) * 1000) : data,
            vitalsScore: rating,
            label: navigatorInformation.isLowEndDevice ? 'Low End Device' : 'High End Device',
            nonInteraction: 1
          };
          if (isValidEvent(metricName)) {
            tc.track(events[metricName], { addToTrackingEvent: eventData });
          }
        }
      });
    } catch {
      // Intentionally doing nothing here.
    }
  });
